import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import FaqItem from './FaqItem';
import { useSEO } from '../../../hooks/useSEO';
import { SITE_URL, LOGO_URL } from '../../../utils/siteConfig';

export interface BlogSection {
  id: string;
  heading: string;
  subheading?: string;
  content: string | string[];
  highlight?: { label: string; value: string }[];
  tip?: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  metaDescription: string;
  metaKeywords: string;
  heroImage: string;
  heroImageAlt: string;
  category: string;
  readTime: string;
  inkoopprijs: string;
  verkoopprijs: string;
  marge: string;
  productLabel: string;
  intro: string;
  sections: BlogSection[];
  faqs: { q: string; a: string }[];
  ctaTitle: string;
  ctaText: string;
  relatedPosts: { title: string; href: string; label: string }[];
}

interface Props {
  post: BlogPost;
}

export default function BlogPostLayout({ post }: Props) {
  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });

  useSEO({
    title: `${post.title} | DUC - Direct Uit China`,
    description: post.metaDescription,
    keywords: post.metaKeywords + ', inkopen uit China, product sourcing, import China, DUC Direct Uit China',
    canonical: `${SITE_URL}/blog/${post.slug}`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': `${SITE_URL}/blog/${post.slug}#article`,
        headline: post.title,
        description: post.metaDescription,
        image: {
          '@type': 'ImageObject',
          url: post.heroImage,
          width: 1200,
          height: 630,
        },
        author: { '@type': 'Organization', name: 'Direct Uit China (DUC)', url: SITE_URL },
        publisher: {
          '@type': 'Organization',
          name: 'Direct Uit China (DUC)',
          logo: { '@type': 'ImageObject', url: LOGO_URL, width: 800, height: 600 },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
        url: `${SITE_URL}/blog/${post.slug}`,
        keywords: post.metaKeywords,
        inLanguage: 'nl-NL',
        datePublished: '2026-03-26',
        dateModified: '2026-03-26',
        articleSection: post.category,
        about: {
          '@type': 'Thing',
          name: post.productLabel,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: post.productLabel, item: `${SITE_URL}/blog/${post.slug}` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  });

  const paragraphs = (content: string | string[]) =>
    Array.isArray(content) ? content : [content];

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={post.heroImage}
            alt={post.heroImageAlt}
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/75 to-[#0A1628]/40" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 w-full">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4 }}>
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/45 hover:text-[#3B9EE8] text-sm mb-6 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line" />
              Terug naar blog
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: 0.1 }} className="flex flex-wrap items-center gap-3 mb-6">
            <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-1.5 text-[#3B9EE8] text-xs font-600">
              <i className="ri-price-tag-3-line" />
              {post.category}
            </span>
            <span className="text-white/35 text-xs flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-time-line text-xs" />
              {post.readTime} leestijd
            </span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.15 }} className="font-heading font-800 text-white text-3xl md:text-4xl lg:text-5xl leading-tight mb-6">
            {post.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.25 }} className="text-white/60 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
            {post.intro}
          </motion.p>
          {/* Marges bar */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.3 }} className="flex flex-wrap gap-6 pt-7 mt-7 border-t border-white/10">
            {[
              { label: 'Inkoopprijs China', value: post.inkoopprijs, icon: 'ri-building-line' },
              { label: 'Verkoopprijs NL', value: post.verkoopprijs, icon: 'ri-store-line' },
              { label: 'Marge', value: post.marge, icon: 'ri-line-chart-line' },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B9EE8]/15">
                  <i className={`${s.icon} text-[#3B9EE8] text-sm`} />
                </div>
                <div>
                  <div className="font-heading font-800 text-white text-base">{s.value}</div>
                  <div className="text-white/40 text-xs">{s.label}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Article body */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          {post.sections.map((section, idx) => (
            <section key={section.id} className={`mb-14 ${idx > 0 ? 'pt-10 border-t border-[#0A1628]/6' : ''}`}>
              <h2 className="font-heading font-800 text-[#0A1628] text-2xl md:text-3xl leading-tight mb-2">
                {section.heading}
              </h2>
              {section.subheading && (
                <p className="text-[#3B9EE8] font-600 text-sm mb-5">{section.subheading}</p>
              )}
              <div className="space-y-4 mt-5">
                {paragraphs(section.content).map((p, i) => (
                  <p key={i} className="text-[#0A1628]/65 text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
              {section.highlight && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {section.highlight.map((h) => (
                    <div key={h.label} className="bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl p-5 text-center">
                      <div className="font-heading font-800 text-[#3B9EE8] text-xl mb-1">{h.value}</div>
                      <div className="text-[#0A1628]/55 text-xs">{h.label}</div>
                    </div>
                  ))}
                </div>
              )}
              {section.tip && (
                <div className="mt-6 flex items-start gap-4 bg-[#3B9EE8]/8 border border-[#3B9EE8]/25 rounded-xl p-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B9EE8]/20 flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-[#3B9EE8] text-sm" />
                  </div>
                  <p className="text-[#0A1628]/75 text-sm leading-relaxed italic">{section.tip}</p>
                </div>
              )}
            </section>
          ))}

          {/* FAQ */}
          <section className="mb-14 pt-10 border-t border-[#0A1628]/6">
            <h2 className="font-heading font-800 text-[#0A1628] text-2xl md:text-3xl mb-8">
              Veelgestelde vragen
            </h2>
            <div className="space-y-2">
              {post.faqs.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} />
              ))}
            </div>
          </section>

          {/* CTA inline */}
          <div className="bg-[#0A1628] rounded-2xl p-8 md:p-10 mb-14">
            <h3 className="font-heading font-800 text-white text-xl md:text-2xl mb-3">{post.ctaTitle}</h3>
            <p className="text-white/55 text-base leading-relaxed mb-7 max-w-xl">{post.ctaText}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-6 py-3 rounded-lg font-700 text-sm transition-all cursor-pointer whitespace-nowrap">
                Vraag gratis offerte aan
                <i className="ri-arrow-right-line" />
              </Link>
              <a href="https://wa.me/31624359162?text=Hi%2C%20ik%20wil%20graag%20een%20offerte%20aanvragen." target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-6 py-3 rounded-lg font-600 text-sm transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-whatsapp-line text-base" />
                WhatsApp DUC
              </a>
            </div>
          </div>

          {/* Related posts */}
          {post.relatedPosts.length > 0 && (
            <section className="pt-10 border-t border-[#0A1628]/6">
              <h3 className="font-heading font-700 text-[#0A1628] text-lg mb-6">Meer winning products</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {post.relatedPosts.map((r) => (
                  <Link key={r.href} to={r.href} className="flex items-start gap-3 bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl p-4 hover:border-[#3B9EE8]/40 hover:bg-[#3B9EE8]/5 transition-all cursor-pointer group">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12 flex-shrink-0 mt-0.5">
                      <i className="ri-article-line text-[#3B9EE8] text-sm" />
                    </div>
                    <div>
                      <span className="text-[#3B9EE8] text-xs font-600 block mb-1">{r.label}</span>
                      <span className="text-[#0A1628]/75 text-sm font-500 leading-snug group-hover:text-[#0A1628] transition-colors">{r.title}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
    </main>
  );
}
